import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details'];

  #touchSession = null;
  #swipePanels = [];
  #dragThreshold = 0.1;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.#setupAnimatedElementListeners();
    this.#setupSwipeToClose();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
    this.#teardownSwipeToClose();
  }

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   */
  toggle() {
    return this.isOpen ? this.close() : this.open();
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {Event} [event]
   */
  open(event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'true');
    requestAnimationFrame(() => {
      details.classList.add('menu-open');
      setTimeout(() => {
        trapFocus(details);
      }, 0);
    });
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');

    onAnimationEnd(details, () => {
      reset(details);

      if (details === this.refs.details) {
        removeTrapFocus();
        const openDetails = this.querySelectorAll('details[open]');
        openDetails.forEach(reset);
      } else {
        setTimeout(() => {
          trapFocus(this.refs.details);
        }, 0);
      }
    });
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    /**
     * @param {AnimationEvent} event
     */
    function removeWillChangeOnAnimationEnd(event) {
      const target = event.target;
      if (target && target instanceof HTMLElement) {
        target.style.setProperty('will-change', 'unset');
        target.removeEventListener('animationend', removeWillChangeOnAnimationEnd);
      }
    }
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  #setupSwipeToClose() {
    this.#swipePanels = Array.from(this.querySelectorAll('[data-header-drawer], .menu-drawer__submenu')).filter(
      (element) => element instanceof HTMLElement
    );

    this.#swipePanels.forEach((panel) => {
      panel.addEventListener('touchstart', this.#onTouchStart, { passive: true });
      panel.addEventListener('touchmove', this.#onTouchMove, { passive: true });
      panel.addEventListener('touchend', this.#onTouchEnd, { passive: true });
      panel.addEventListener('touchcancel', this.#onTouchCancel, { passive: true });
    });
  }

  #teardownSwipeToClose() {
    this.#swipePanels.forEach((panel) => {
      panel.removeEventListener('touchstart', this.#onTouchStart);
      panel.removeEventListener('touchmove', this.#onTouchMove);
      panel.removeEventListener('touchend', this.#onTouchEnd);
      panel.removeEventListener('touchcancel', this.#onTouchCancel);
    });

    this.#swipePanels = [];
  }

  #onTouchStart = (event) => {
    if (!this.isOpen) return;
    if (window.matchMedia('(min-width: 750px)').matches) return;

    const touch = event.touches[0];
    const panel = event.currentTarget;
    if (!touch) return;
    if (!(panel instanceof HTMLElement)) return;

    this.#touchSession = {
      panel,
      startX: touch.clientX,
      startY: touch.clientY,
      startTime: Date.now(),
      deltaX: 0,
      deltaY: 0,
      swiping: false,
      directionLocked: false,
    };

    panel.style.willChange = 'transform';
    panel.style.transition = 'none';
  };

  #onTouchMove = (event) => {
    if (!this.#touchSession) return;

    const touch = event.touches[0];
    if (!touch) return;

    this.#touchSession.deltaX = touch.clientX - this.#touchSession.startX;
    this.#touchSession.deltaY = touch.clientY - this.#touchSession.startY;

    if (this.#touchSession.deltaX >= 0) return;

    if (
      !this.#touchSession.directionLocked &&
      Math.abs(this.#touchSession.deltaX) > 10 &&
      Math.abs(this.#touchSession.deltaX) > Math.abs(this.#touchSession.deltaY) * 0.7
    ) {
      this.#touchSession.directionLocked = true;
    }

    if (!this.#touchSession.directionLocked && Math.abs(this.#touchSession.deltaY) > Math.abs(this.#touchSession.deltaX)) {
      return;
    }

    this.#touchSession.swiping = true;
    const dragX = Math.max(this.#touchSession.deltaX, -window.innerWidth);
    this.#applyDragProgress(this.#touchSession.panel, dragX);
  };

  #onTouchEnd = () => {
    if (!this.#touchSession) return;

    const { panel, deltaX, deltaY, swiping, startTime } = this.#touchSession;
    this.#touchSession = null;

    if (!swiping) return;
    if (Math.abs(deltaY) > 90 && Math.abs(deltaY) > Math.abs(deltaX) * 1.3) {
      this.#animatePanelBack(panel);
      return;
    }

    const duration = Math.max(1, Date.now() - startTime);
    const swipeVelocity = Math.abs(deltaX) / duration;
    const panelWidth = panel.getBoundingClientRect().width || window.innerWidth;
    const passedDistance = Math.abs(deltaX) >= panelWidth * this.#dragThreshold;
    const passedVelocity = deltaX <= -12 && swipeVelocity > 0.18;

    if (!passedDistance && !passedVelocity) {
      this.#animatePanelBack(panel);
      return;
    }

    this.#animatePanelOut(panel, () => {
      const openDetails = panel.closest('details[open]');

      if (openDetails instanceof HTMLDetailsElement && openDetails !== this.refs.details) {
        this.back({ target: openDetails });
        return;
      }

      this.close();
    });
  };

  #onTouchCancel = () => {
    if (this.#touchSession?.panel instanceof HTMLElement) {
      this.#animatePanelBack(this.#touchSession.panel);
    }
    this.#touchSession = null;
  };

  #applyDragProgress(panel, dragX) {
    const drawer = this.querySelector('[data-header-drawer]');
    const backdrop = this.querySelector('.menu-drawer__backdrop');
    const panelWidth = panel.getBoundingClientRect().width || window.innerWidth;
    const progress = Math.min(1, Math.abs(dragX) / panelWidth);
    const backdropOpacity = Math.max(0, 1 - progress * 1.35);

    panel.style.transform = `translateX(${dragX}px)`;

    if (panel === drawer && backdrop instanceof HTMLElement) {
      backdrop.style.opacity = `${backdropOpacity}`;
    }
  }

  #animatePanelBack(panel) {
    const drawer = this.querySelector('[data-header-drawer]');
    const backdrop = this.querySelector('.menu-drawer__backdrop');

    panel.style.transition = 'transform 180ms ease-out';
    panel.style.transform = 'translateX(0)';

    if (panel === drawer && backdrop instanceof HTMLElement) {
      backdrop.style.transition = 'opacity 180ms ease-out';
      backdrop.style.opacity = '1';
    }

    window.setTimeout(() => {
      panel.style.transition = '';
      panel.style.willChange = '';
      if (panel === drawer && backdrop instanceof HTMLElement) {
        backdrop.style.transition = '';
        backdrop.style.opacity = '';
      }
    }, 200);
  }

  #animatePanelOut(panel, onComplete) {
    const drawer = this.querySelector('[data-header-drawer]');
    const backdrop = this.querySelector('.menu-drawer__backdrop');

    panel.style.transition = 'transform 180ms ease-out';
    panel.style.transform = 'translateX(-100%)';

    if (panel === drawer && backdrop instanceof HTMLElement) {
      backdrop.style.transition = 'opacity 180ms ease-out';
      backdrop.style.opacity = '0';
    }

    window.setTimeout(() => {
      panel.style.transition = '';
      panel.style.transform = '';
      panel.style.willChange = '';
      if (panel === drawer && backdrop instanceof HTMLElement) {
        backdrop.style.transition = '';
        backdrop.style.opacity = '';
      }
      onComplete();
    }, 190);
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}